import React, { Fragment, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { images as imageApi } from '../../utils/api';

const ImageViewerModal = ({
  selectedImage,
  onClose,
  onNavigate
}) => {
  const { index, images, coin, isShared } = selectedImage;

  const handlePopState = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const newIndex = index === 0 ? images.length - 1 : index - 1;
        onNavigate(newIndex);
      } else if (e.key === 'ArrowRight') {
        const newIndex = index === images.length - 1 ? 0 : index + 1;
        onNavigate(newIndex);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [index, images.length, onNavigate]);

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-90 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel 
                className="relative transform overflow-hidden rounded-lg bg-black text-left shadow-xl transition-all w-full max-w-5xl h-[90vh]"
                onClick={e => e.stopPropagation()}
              >
                {/* Close button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className="absolute top-4 right-4 text-white hover:text-gray-300"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="w-full h-full flex flex-col p-4">
                  {/* Image */}
                  <div className="flex-1 flex items-center justify-center min-h-0">
                    <img
                      src={imageApi.getImage(images[index].file_path, images[index].source)}
                      alt={`${coin.year} ${coin.denomination} - Full size image ${index + 1}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>

                  {/* Navigation and counter */}
                  {images.length > 1 && (
                    <div className="flex items-center justify-center gap-8 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newIndex = index === 0 
                            ? images.length - 1 
                            : index - 1;
                          onNavigate(newIndex);
                        }}
                        className="text-white hover:text-gray-300"
                      >
                        <span className="sr-only">Previous image</span>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      <div className="text-white text-sm">
                        Image {index + 1} of {images.length}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newIndex = index === images.length - 1 
                            ? 0 
                            : index + 1;
                          onNavigate(newIndex);
                        }}
                        className="text-white hover:text-gray-300"
                      >
                        <span className="sr-only">Next image</span>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImageViewerModal;
